/**
 * Turns the studio's column shelf into explicit column requests. A draft has no saved `column_tuples`,
 * and only explicit requests can state a variance (`calc`/`left`/`right`) or an ex FX restatement.
 *
 * Labels match the engine's `"<scenario> <currency> <period>"` spelling because a variance addresses
 * its sides by label; any other spelling silently drops the variance column.
 */

import type { ColumnAxisEntry, ColumnRequest } from 'src/components/star-grid/api/reportEngineTypes';
import type { StudioCatalog, StudioColumn, StudioPeriod, StudioScenarioBlock } from 'src/components/star-grid/reportStudio.types';
import { SPACER_CALC } from 'src/components/star-grid/utils/reportPivotModel';

/** How the deck spells a separator tuple: no scenario, no period. `is_spacer_tuple` reads the scenario. */
const SPACER_SCENARIO = 'EMPTY';
const SPACER_PERIOD = '000000';

/** Fallback when a column names no currency; every report stores `measure_usd`. */
export const DEFAULT_CURRENCY = 'USD';

/** The calc a variance column carries, and its percentage twin. The engine parses both. */
export const VARIANCE_CALC = 'variance';
export const VARIANCE_PCT_CALC = 'variance_pct';
/** The direction a variance defaults to; only a non-default direction is persisted. */
const DEFAULT_DIRECTION = 'base_minus_compare';

/**
 * The period token a month list spells: one month, a whole quarter, or a whole year. `undefined` for
 * any other shape — the engine accepts only those three, and a made-up token would either be
 * rejected or, worse, name a different span than the chips the user clicked.
 */
export function periodToken(months: readonly number[]): string | undefined {
  const ids = Array.from(new Set(months)).sort((a, b) => a - b);
  if (ids.length === 0) return undefined;
  if (ids.length === 1) return String(ids[0]);
  const years = new Set(ids.map((month) => Math.floor(month / 100)));
  if (years.size !== 1) return undefined;
  const year = ids[0] === undefined ? 0 : Math.floor(ids[0] / 100);
  const numbers = ids.map((month) => month % 100);
  const contiguous = numbers.every((value, index) => value === numbers[0]! + index);
  if (numbers.length === 12 && numbers[0] === 1) return String(year);
  if (numbers.length === 3 && contiguous && numbers[0]! % 3 === 1) return `${year}Q${(numbers[0]! - 1) / 3 + 1}`;
  return undefined;
}

/** `"<scenario> <currency> <period>"` — the label the engine gives a base column. */
export function columnBaseLabel(scenario: string, currency: string, period: string): string {
  return `${scenario} ${currency} ${period}`.trim();
}

/**
 * The `@` form the engine parses, e.g. `USD@GQ3-26`. A "Constant FX" column is restated at the
 * frozen rates of the scenario it is being read against: its own scenario for a base column, the
 * COMPARE scenario for a variance. `USD` is the target currency, the only one `measure_usd` reports in.
 */
export function fxScenarioFor(scenario: string): string {
  return `${DEFAULT_CURRENCY}@${scenario}`;
}

/**
 * The scenario whose rates an ex FX column is held at, read off the chosen policy.
 *
 * The policy id is a scenario id, so it resolves through the same lookup as any other scenario. Only
 * its truthiness used to be read, which is why picking a policy changed nothing.
 */
function fxScenarioOf(fxPolicyId: string | undefined, lookup: Lookup): string | undefined {
  return fxPolicyId ? lookup.scenario(fxPolicyId) : undefined;
}

interface Lookup {
  readonly scenario: (id: string) => string | undefined;
  readonly currency: (id: string | undefined) => string;
  readonly period: (id: string) => StudioPeriod | undefined;
}

function lookups(catalog: StudioCatalog, datasetId: string): Lookup {
  const scenarios = new Map(catalog.scenarios.map((option) => [option.id, option.label]));
  const periods = new Map(catalog.periods.map((period) => [period.id, period]));
  const dataset = catalog.datasets.find((entry) => entry.id === datasetId);
  const currencies = new Map((dataset?.currencies ?? []).map((option) => [option.id, option.label]));
  return {
    scenario: (id) => scenarios.get(id),
    currency: (id) => (id ? currencies.get(id) ?? id : DEFAULT_CURRENCY),
    period: (id) => periods.get(id)
  };
}

/** The `(label, period token)` pairs one scenario block resolves to, in shelf order. */
function baseColumnsOf(
  block: StudioScenarioBlock,
  lookup: Lookup
): { readonly scenario: string; readonly currency: string; readonly tokens: readonly string[] } | undefined {
  const scenario = lookup.scenario(block.scenario_id);
  if (!scenario) return undefined;
  const currency = lookup.currency(block.currency_ids[0]);
  const tokens: string[] = [];
  for (const periodId of block.period_ids) {
    const token = periodToken(lookup.period(periodId)?.months ?? []);
    if (token) tokens.push(token);
  }
  return tokens.length > 0 ? { scenario, currency, tokens } : undefined;
}

/**
 * Column requests for a studio draft's columns.
 *
 * A block the engine cannot express is DROPPED rather than approximated: an attribute column has no
 * engine equivalent, and a period whose month list is not a month/quarter/year has no token. Dropping
 * loses a column, which is visible; approximating produces a column of wrong numbers, which is not.
 */
export function studioColumnRequests(catalog: StudioCatalog, datasetId: string, columns: readonly StudioColumn[]): ColumnRequest[] {
  const lookup = lookups(catalog, datasetId);

  // Resolved up front, WITHOUT emitting: a variance addresses its two sides by base-column label, so
  // every base has to be known before any variance is built — even one sitting left of it on the
  // shelf.
  const bases = new Map<string, { readonly scenario: string; readonly currency: string; readonly tokens: readonly string[] }>();
  for (const column of columns) {
    if (column.kind !== 'scenario') continue;
    const resolved = baseColumnsOf(column, lookup);
    if (resolved) bases.set(column.id, resolved);
  }

  // ONE pass in SHELF order, so a card's position is its position in the grid.
  //
  // This used to be two passes filtered by `kind` — every scenario column, then every variance — which
  // threw the shelf order away. The deck interleaves them (`WW Report` runs four Actuals-25 months, a
  // spacer, four OP2-26, ... then its variance blocks) and `_column_requests` reads that straight off
  // `spec.column_tuples`, so the saved report renders in deck order while the preview grouped all the
  // variances at the far right. `_explicit_columns` keeps this payload's order verbatim, so the order
  // built here IS the preview's order. `resolve_columns` defers variances and then restores request
  // position, so emitting one mid-list resolves correctly and still lands where the card sits.
  const out: ColumnRequest[] = [];
  /**
   * A blank separator between column CARDS, so the preview draws the vertical rule between scenario
   * groups that the deck and the saved report both draw.
   *
   * The deck authors these gaps as real tuples (`['EMPTY','EMPTY','000000']`) and `_column_requests`
   * emits them for a saved report, but the preview sends EXPLICIT columns built here — which had no
   * spacer, so the groups ran together with no line between them. `_explicit_columns` forwards `calc`
   * verbatim, and the renderer already treats `calc: 'spacer'` as a gap rather than a value column.
   */
  const pushSpacer = (): void => {
    if (out.length > 0) out.push({ label: '', calc: SPACER_CALC });
  };
  for (const column of columns) {
    if (column.kind === 'scenario') {
      const resolved = bases.get(column.id);
      if (!resolved) continue;
      pushSpacer();
      for (const token of resolved.tokens) {
        out.push({
          label: columnBaseLabel(resolved.scenario, resolved.currency, token),
          members: [[resolved.scenario, token]],
          currency: resolved.currency,
          // Absent for "Scenario default", which is what keeps that column on the untouched
          // `measure_usd` path and its SQL byte-identical to the no-FX case.
          //
          // `fx_policy_id` names WHICH scenario's rates to hold; it is not the column's own. Restating
          // a column at its own rates is arithmetically a no-op, so ex FX on a base column did nothing.
          ...(column.fx_policy_id ? { fx_scenario: fxScenarioFor(fxScenarioOf(column.fx_policy_id, lookup) ?? resolved.scenario) } : {})
        });
      }
      continue;
    }
    if (column.kind !== 'variance') continue;
    const base = bases.get(column.base_block_id);
    const compare = bases.get(column.compare_block_id);
    if (!base || !compare) continue;
    pushSpacer();
    for (const pair of column.period_pairs) {
      const baseToken = periodToken(lookup.period(pair.base)?.months ?? []);
      const compareToken = periodToken(lookup.period(pair.compare)?.months ?? []);
      if (!baseToken || !compareToken) continue;
      const baseLabel = columnBaseLabel(base.scenario, base.currency, baseToken);
      const compareLabel = columnBaseLabel(compare.scenario, compare.currency, compareToken);
      // `left - right`, so the direction decides which side is which rather than negating after.
      const swapped = column.direction === 'compare_minus_base';
      const label = `${base.scenario} vs ${compare.scenario} ${baseToken}`;
      const left = swapped ? compareLabel : baseLabel;
      const right = swapped ? baseLabel : compareLabel;
      // The ex FX vintage follows the right-hand side, which is what the variance reads against.
      const fx = column.fx_policy_id ? { fx_scenario: fxScenarioFor(swapped ? base.scenario : compare.scenario) } : {};
      // Persisted only when non-default, so an untouched base_minus_compare variance stays identical
      // and reopen still infers `left = base`.
      const dir = column.direction !== DEFAULT_DIRECTION ? { variance_direction: column.direction } : {};
      out.push({ label, calc: VARIANCE_CALC, currency: base.currency, left, right, ...fx, ...dir });
      // The pct twin reads the same two sides; `"<variance> %"` is the label the engine's
      // `parse_variance_label` recognises as the percentage form of that variance.
      if (column.show_percent) {
        out.push({ label: `${label} %`, calc: VARIANCE_PCT_CALC, currency: base.currency, left, right, ...fx, ...dir });
      }
    }
  }
  return out;
}

/** The three column-axis position names. `spec.column_role` reads the role off the NAME. */
export const COLUMN_POSITIONS = ['FPA Deck Scenario', 'Rpt Currency', 'Time Monthly'] as const;

/**
 * The persisted form of the same columns: a positional axis plus its tuples.
 *
 * A saved spec has no explicit column list — the engine crosses `column_axis` (scenario x currency x
 * period) with `column_tuples`, so reopening a report has to find its columns there. A base column's
 * ex FX restatement rides its CURRENCY member as `USD@<scenario>`, which is the same string the deck
 * itself stores, so no per-tuple field is needed for it; a derived column keeps its own entry.
 */
export function studioColumnAxis(requests: readonly ColumnRequest[]): {
  readonly column_axis: ColumnAxisEntry[];
  readonly column_tuples: string[][];
} {
  const tuples: string[][] = [];
  for (const request of requests) {
    // A SEPARATOR is persisted the way the DECK persists one: a tuple whose scenario slot is `EMPTY`,
    // which `is_spacer_tuple` already recognises server-side. Skipping it entirely is why a saved report
    // lost the vertical rule between column groups that the deck's own reports have.
    if (request.calc === SPACER_CALC) {
      tuples.push([SPACER_SCENARIO, DEFAULT_CURRENCY, SPACER_PERIOD]);
      continue;
    }
    if (request.calc) continue;
    const pair = request.members?.[0];
    if (!pair) continue;
    tuples.push([pair[0], request.fx_scenario ?? request.currency ?? DEFAULT_CURRENCY, pair[1]]);
  }
  const distinct = (index: number): string[] => Array.from(new Set(tuples.map((values) => values[index]!)));
  const column_axis: ColumnAxisEntry[] = tuples.length
    ? COLUMN_POSITIONS.map((dimension, index) => ({ dimension, hierarchy: dimension, members: distinct(index) }))
    : [];
  // POSITION each derived column by how many tuples precede it, so the saved report renders it where the
  // card sits. `position` is read as "emit before tuple N"; with none the engine emitted every derived
  // column ahead of the tuples (or trailing them), which is why a variance card in the middle of the
  // shelf came back at one end after saving.
  let tuplesBefore = 0;
  for (const request of requests) {
    if (!request.calc) {
      if (request.members?.[0]) tuplesBefore += 1;
      continue;
    }
    // A SPACER is not a calc column, but it IS a tuple, so it still advances the position counter.
    if (request.calc === SPACER_CALC) {
      tuplesBefore += 1;
      continue;
    }
    column_axis.push({
      position: tuplesBefore,
      dimension: COLUMN_POSITIONS[0],
      hierarchy: COLUMN_POSITIONS[0],
      members: [request.label],
      calc: request.calc,
      left: request.left ?? null,
      right: request.right ?? null,
      ...(request.fx_scenario ? { fx_scenario: request.fx_scenario } : {}),
      ...(request.variance_direction ? { variance_direction: request.variance_direction } : {})
    });
  }
  return { column_axis, column_tuples: tuples };
}
